import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../components/ui/Button';

const QRComparePage = () => {
  const [fileA, setFileA] = useState(null);
  const [fileB, setFileB] = useState(null);
  const [isDecoding, setIsDecoding] = useState(false);
  const [resultA, setResultA] = useState({ content: '', method: '', error: '' });
  const [resultB, setResultB] = useState({ content: '', method: '', error: '' });

  const match = useMemo(() => {
    const a = (resultA?.content || '').trim();
    const b = (resultB?.content || '').trim();
    if (!a || !b) return null;
    return a === b;
  }, [resultA?.content, resultB?.content]);

  const decodeWithQrScanner = async (img) => {
    const { QrScanner } = await import('qr-scanner');
    const content = await QrScanner.scanImage(img, null, null, null, {
      returnDetailedScanResult: false,
      scanRegion: null,
    });
    return content || '';
  };

  const decodeWithJsQr = async (img) => {
    const jsQR = await import('jsqr');

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';

    const width = Math.min(img.naturalWidth || img.width || 512, 1024);
    const height = Math.min(img.naturalHeight || img.height || 512, 1024);
    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(img, 0, 0, width, height);

    const imageData = ctx.getImageData(0, 0, width, height);
    const code = jsQR.default(imageData.data, imageData.width, imageData.height, {
      inversionAttempts: 'attemptBoth',
    });
    return code?.data || '';
  };

  const fileToImage = async (file) => {
    const url = URL.createObjectURL(file);
    try {
      const img = new Image();
      img.decoding = 'async';
      img.src = url;
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });
      return img;
    } finally {
      // Don't revoke here; decoding libs may read it async. We'll revoke after decode().
    }
  };

  const decodeFile = async (file) => {
    const url = URL.createObjectURL(file);
    try {
      const img = new Image();
      img.decoding = 'async';
      img.src = url;
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });

      try {
        const content = await decodeWithQrScanner(img);
        if (content) return { content, method: 'qr-scanner', error: '' };
      } catch (e) {
        // fallback below
        console.log('qr-scanner decode failed:', e?.message);
      }

      try {
        const content = await decodeWithJsQr(img);
        if (content) return { content, method: 'jsqr', error: '' };
      } catch (e) {
        console.log('jsqr decode failed:', e?.message);
      }

      return { content: '', method: '', error: 'Kon geen QR-data uit deze afbeelding lezen.' };
    } catch (e) {
      return { content: '', method: '', error: e?.message || 'Afbeelding kon niet geladen worden.' };
    } finally {
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  };

  const handleDecode = async () => {
    setResultA({ content: '', method: '', error: '' });
    setResultB({ content: '', method: '', error: '' });
    if (!fileA || !fileB) return;

    setIsDecoding(true);
    try {
      const [a, b] = await Promise.all([decodeFile(fileA), decodeFile(fileB)]);
      setResultA(a);
      setResultB(b);
    } finally {
      setIsDecoding(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">QR Compare (Debug)</h1>
              <p className="text-sm text-gray-600 mt-1">
                Upload 2 bestanden (PNG/JPG/SVG). We decoderen beide en tonen of de inhoud identiek is.
              </p>
            </div>
            <Link
              className="text-sm font-medium text-blue-600 hover:text-blue-500 transition-colors"
              to="/"
            >
              Terug
            </Link>
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Bestand A</label>
              <input
                type="file"
                accept=".png,.jpg,.jpeg,.svg"
                onChange={(e) => setFileA(e?.target?.files?.[0] || null)}
              />
              {fileA && <div className="mt-2 text-xs text-gray-500">{fileA.name}</div>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Bestand B</label>
              <input
                type="file"
                accept=".png,.jpg,.jpeg,.svg"
                onChange={(e) => setFileB(e?.target?.files?.[0] || null)}
              />
              {fileB && <div className="mt-2 text-xs text-gray-500">{fileB.name}</div>}
            </div>
          </div>

          <div className="mt-6 flex items-center gap-3">
            <Button
              type="button"
              onClick={handleDecode}
              disabled={!fileA || !fileB || isDecoding}
              className="flex items-center justify-center"
            >
              {isDecoding ? 'Decoderen...' : 'Decode & compare'}
            </Button>
            {match !== null && (
              <div
                className={`text-sm font-medium ${
                  match ? 'text-green-700' : 'text-red-700'
                }`}
              >
                {match ? 'Match: JA' : 'Match: NEE'}
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-sm font-semibold text-gray-900">Resultaat A</h2>
            {resultA?.error ? (
              <div className="mt-2 text-sm text-red-600">{resultA.error}</div>
            ) : (
              <>
                <div className="mt-2 text-xs text-gray-500">Methode: {resultA?.method || '-'}</div>
                <pre className="mt-3 text-xs bg-gray-50 border border-gray-200 rounded p-3 whitespace-pre-wrap break-words">
                  {resultA?.content || ''}
                </pre>
              </>
            )}
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-sm font-semibold text-gray-900">Resultaat B</h2>
            {resultB?.error ? (
              <div className="mt-2 text-sm text-red-600">{resultB.error}</div>
            ) : (
              <>
                <div className="mt-2 text-xs text-gray-500">Methode: {resultB?.method || '-'}</div>
                <pre className="mt-3 text-xs bg-gray-50 border border-gray-200 rounded p-3 whitespace-pre-wrap break-words">
                  {resultB?.content || ''}
                </pre>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRComparePage;

